import { useCallback, useEffect, useMemo, useState } from 'react'

import { useGetUserSettingsQuery } from '../api/combined'
import { SubgenreMap } from '../features/account'
import analyticsService, { ITrackEventOptions } from '../services/AnalyticsService'
import { AnalyticsProviders } from '../types/IAnalyticsProvider'

export const useSubgenrePicker = (
  initialSubgenres?: SubgenreMap,
  trackEventId?: string,
  hasDefaultSubgenre?: boolean,
  externalSetter?: (subgenres: SubgenreMap) => void
) => {
  const { data: userSettings } = useGetUserSettingsQuery()

  const usingExplicitInitial = useMemo(
    () => !!((initialSubgenres && Object.keys(initialSubgenres).length) || hasDefaultSubgenre),
    [hasDefaultSubgenre, initialSubgenres]
  )

  const defaultSubgenres = useMemo(() => {
    if (usingExplicitInitial) return initialSubgenres
    return userSettings?.userDefaultConventionalSubgenres || {}
  }, [usingExplicitInitial, initialSubgenres, userSettings?.userDefaultConventionalSubgenres])

  const [subgenres, setSubgenres] = useState<SubgenreMap>(defaultSubgenres || {})
  const [userTouched, setUserTouched] = useState(false)

  // When the saved default subgenres update on the server (e.g. another
  // browser tab changed them), and the user hasn't actively modified the
  // picker in this session, mirror the latest saved default into local
  // state. This prevents the picker from showing a stale default value
  // that disagrees with the server, which is what made the "Set as
  // Default" button incorrectly remain enabled (GR-2488).
  useEffect(() => {
    if (usingExplicitInitial || userTouched) return
    const fresh = userSettings?.userDefaultConventionalSubgenres ?? {}
    if (JSON.stringify(subgenres) === JSON.stringify(fresh)) return

    setSubgenres(fresh)
    if (externalSetter) externalSetter(fresh)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usingExplicitInitial, userTouched, userSettings?.userDefaultConventionalSubgenres])

  const handleSubgenresChange = useCallback(
    (subgenres: SubgenreMap) => {
      if (trackEventId) {
        analyticsService.trackEvent(`${trackEventId}: Filtered Games`, {
          data: {
            typeId: Object.keys(subgenres),
          },
          serviceToExclude: [AnalyticsProviders.hubspot],
        } as ITrackEventOptions)
      }
      setUserTouched(true)
      setSubgenres(subgenres)
      if (externalSetter) externalSetter(subgenres)
    },
    [externalSetter, trackEventId]
  )

  return { subgenres, handleSubgenresChange }
}
